// SCR-STAFF-967 (AP-ENG-02؛ US-ENG-070 تا 073): نظرسنجی و بازخورد — پاسخ‌ها و شاخص NPS (AC-ENG-073.1)، موارد بازخورد منفی با تیکت CHN (BR-ENG-071)،
// نظرسنجی‌ها (پرسش‌های استاندارد NPS و رضایت + پرسش‌های قالب DOC؛ AC-ENG-070.1) و تنظیمات ارسال (فاصله پس از بستن ویزیت، حداکثر یک‌بار در ۳۰ روز،
// اعتبار لینک و دعوت به نظر عمومی بدون مشوق — BR-ENG-070 و BR-ENG-072). مشاهده جزئیات پاسخ در Audit ثبت می‌شود.
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type EngFeedbackCaseStatus,
  type EngFeedbackCaseView,
  type EngReviewLink,
  type EngSurveyAnswerView,
  type EngSurveyResponseSummaryView,
  type EngSurveySettingsView,
  type EngSurveyTemplateOption,
  type EngSurveyText,
  type EngSurveyView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  latinDigits,
  LoadingState,
  PageHeader,
  SampleBadge,
  StatusChip,
  type DataTableColumn,
} from '@phoenix/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { WriteNotice } from '../settings/common';
import { engKeys } from './common';

export const SCR_STAFF_967 = 'SCR-STAFF-967';
export const SURVEYS_PATH = '/crm/surveys';

type SurveyTab = 'responses' | 'feedback' | 'surveys' | 'settings';

const surveyKeys = {
  all: (tenantId: string | undefined) => [...engKeys.all(tenantId), 'surveys'] as const,
};

/** متن چندزبانه پرسش ← زبان جاری (fa پیش‌فرض). */
export function surveyText(text: EngSurveyText | null | undefined, language: string): string {
  if (!text) {
    return '';
  }
  const base = language.split('-')[0];
  return (base === 'en' ? text.en : base === 'ar' ? text.ar : text.fa) || text.fa;
}

/** دسته NPS: مروج ۹–۱۰، خنثی ۷–۸، منتقد ۰–۶. */
export function npsBand(score: number | null | undefined): 'promoter' | 'passive' | 'detractor' | null {
  if (score === null || score === undefined) {
    return null;
  }
  return score >= 9 ? 'promoter' : score >= 7 ? 'passive' : 'detractor';
}

function NpsChip({ score }: { score: number | string | null | undefined }) {
  const { t } = useTranslation('eng');
  const value = score === null || score === undefined ? null : Number(score);
  const band = npsBand(value);
  if (!band || value === null) {
    return <span>—</span>;
  }
  return (
    <Chip
      size="small"
      color={band === 'promoter' ? 'success' : band === 'passive' ? 'warning' : 'error'}
      variant={band === 'passive' ? 'outlined' : 'filled'}
      label={`${faDigits(value)} · ${t(`eng:nps_band.${band}`)}`}
      data-testid="nps-chip"
    />
  );
}

export function SurveysPage() {
  const { t } = useTranslation('eng');
  const access = useAccess();
  const canFeedback = access.can('eng.feedback.manage');
  const canSurvey = access.can('eng.survey.manage');
  const [params, setParams] = useSearchParams();
  const tabs: SurveyTab[] = [
    ...(canFeedback ? (['responses', 'feedback'] as const) : []),
    ...(canSurvey ? (['surveys', 'settings'] as const) : []),
  ];
  const requested = params.get('tab') as SurveyTab | null;
  const tab: SurveyTab = requested && tabs.includes(requested) ? requested : (tabs[0] ?? 'responses');
  const responseId = params.get('response');

  return (
    <section data-screen-id={SCR_STAFF_967}>
      <PageHeader titleKey="eng:surveys.title" subtitleKey="eng:surveys.subtitle" />
      <Tabs
        value={tab}
        onChange={(_event, next: SurveyTab) => setParams({ tab: next }, { replace: true })}
        aria-label={t('eng:surveys.tabs_label')}
        variant="scrollable"
        sx={{ mb: 2 }}
      >
        {tabs.map((value) => (
          <Tab
            key={value}
            value={value}
            label={t(`eng:surveys.tab.${value}`)}
            data-testid={`survey-tab-${value}`}
          />
        ))}
      </Tabs>
      {tab === 'responses' && canFeedback && <ResponsesTab />}
      {tab === 'feedback' && canFeedback && <FeedbackTab />}
      {tab === 'surveys' && canSurvey && <SurveyListTab />}
      {tab === 'settings' && canSurvey && <SettingsTab />}
      {responseId && canFeedback && (
        <ResponseDialog
          id={responseId}
          onClose={() => {
            const next = new URLSearchParams(params);
            next.delete('response');
            setParams(next, { replace: true });
          }}
        />
      )}
    </section>
  );
}

function useOpenResponse() {
  const [params, setParams] = useSearchParams();
  return (id: string) => {
    const next = new URLSearchParams(params);
    next.set('response', id);
    setParams(next, { replace: true });
  };
}

function ResponsesTab() {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const openResponse = useOpenResponse();
  const [negative, setNegative] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const stats = useQuery({
    queryKey: [...surveyKeys.all(session?.tenantId), 'stats'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/engagement/survey-stats')),
  });
  const responses = useQuery({
    queryKey: [...surveyKeys.all(session?.tenantId), 'responses', negative, cursor],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/engagement/survey-responses', {
          params: { query: { negative: negative || undefined, cursor, limit: 50 } },
        }),
      ),
    placeholderData: keepPreviousData,
  });

  const columns: DataTableColumn<EngSurveyResponseSummaryView>[] = [
    {
      id: 'received',
      headerKey: 'eng:surveys.columns.received',
      cell: (row) => <JalaliDate value={row.receivedAt} withTime />,
    },
    { id: 'patient', headerKey: 'eng:surveys.columns.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'branch', headerKey: 'eng:surveys.columns.branch', cell: (row) => row.branchName ?? '—' },
    { id: 'nps', headerKey: 'eng:surveys.columns.nps', cell: (row) => <NpsChip score={row.npsScore} /> },
    {
      id: 'flags',
      headerKey: 'eng:surveys.columns.flags',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          {row.negative && <Chip size="small" color="error" label={t('eng:surveys.negative')} />}
          {row.reviewInvited && (
            <Chip size="small" variant="outlined" label={t('eng:surveys.review_invited')} />
          )}
        </Stack>
      ),
    },
    {
      id: 'actions',
      headerKey: 'eng:surveys.columns.actions',
      cell: (row) => (
        <Button
          size="small"
          onClick={() => openResponse(row.id)}
          data-testid={`survey-response-open-${row.id}`}
        >
          {t('eng:surveys.open')}
        </Button>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      {stats.error && <ErrorState error={stats.error} compact />}
      {stats.data && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' },
            gap: 1.5,
          }}
          data-testid="survey-stats"
        >
          {(
            [
              [
                'nps',
                stats.data.nps === null || stats.data.nps === undefined
                  ? '—'
                  : faDigits(Number(stats.data.nps)),
              ],
              ['sent', faDigits(Number(stats.data.sent))],
              ['responses', faDigits(Number(stats.data.responses))],
              ['promoters', faDigits(Number(stats.data.promoters))],
              ['detractors', faDigits(Number(stats.data.detractors))],
              ['open_feedback', faDigits(Number(stats.data.openFeedback))],
            ] as const
          ).map(([key, value]) => (
            <Paper key={key} variant="outlined" sx={{ p: 1.5 }} data-testid={`survey-stat-${key}`}>
              <Typography variant="caption" color="text.secondary">
                {t(`eng:surveys.stats.${key}`)}
              </Typography>
              <Typography variant="h3" component="p">
                {value}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}
      <FormControlLabel
        control={
          <Switch
            checked={negative}
            onChange={(event) => {
              setCursor(undefined);
              setNegative(event.target.checked);
            }}
          />
        }
        label={t('eng:surveys.only_negative')}
      />
      <DataTable
        captionKey="eng:surveys.responses_caption"
        columns={columns}
        rows={responses.data?.items}
        getRowId={(row) => row.id}
        isLoading={responses.isPending}
        error={responses.error}
        onRetry={() => void responses.refetch()}
        hasMore={Boolean(responses.data?.nextCursor)}
        onLoadMore={() => setCursor(responses.data?.nextCursor ?? undefined)}
        emptyTitleKey="eng:surveys.responses_empty"
      />
    </Stack>
  );
}

function answerText(
  answer: EngSurveyAnswerView,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const value = answer.value as unknown;
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (answer.type === 'Satisfaction' && typeof value === 'number') {
    return t(`eng:satisfaction.${value}`, { defaultValue: faDigits(value) });
  }
  if (answer.type === 'YesNo') {
    return t(value === true ? 'eng:surveys.yes' : 'eng:surveys.no');
  }
  if (typeof value === 'number') {
    return faDigits(value);
  }
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function ResponseDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { t, i18n } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const response = useQuery({
    queryKey: [...surveyKeys.all(session?.tenantId), 'response', id],
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/engagement/survey-responses/{id}', { params: { path: { id } } })),
  });
  const view = response.data;
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="survey-response-title"
      data-screen-id="SCR-STAFF-967-RESPONSE"
    >
      <DialogTitle id="survey-response-title">{t('eng:surveys.response_title')}</DialogTitle>
      <DialogContent dividers>
        {response.isPending && <LoadingState rows={4} />}
        {response.error && <ErrorState error={response.error} onRetry={() => void response.refetch()} />}
        {view && (
          <Stack spacing={1.5} data-testid="survey-response-detail">
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle1">{view.summary.patientName ?? '—'}</Typography>
              <NpsChip score={view.summary.npsScore} />
              {view.summary.negative && <Chip size="small" color="error" label={t('eng:surveys.negative')} />}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {view.summary.branchName ?? '—'} · <JalaliDate value={view.summary.receivedAt} withTime />
              {view.surveyName ? ` · ${view.surveyName}` : ''}
            </Typography>
            {(view.providers.length > 0 || view.services.length > 0) && (
              <Typography variant="body2">
                {[...view.providers, ...view.services]
                  .map((item) => item.name)
                  .filter(Boolean)
                  .join(t('common:list_separator'))}
              </Typography>
            )}
            <List dense aria-label={t('eng:surveys.answers')}>
              {view.answers.map((answer) => (
                <ListItem key={answer.key} disableGutters>
                  <ListItemText
                    primary={surveyText(answer.label, i18n.language)}
                    secondary={answerText(answer, t)}
                  />
                </ListItem>
              ))}
            </List>
            {view.summary.feedbackCaseId && (
              <Button
                component={RouterLink}
                to={`${SURVEYS_PATH}?tab=feedback`}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t('eng:surveys.open_feedback')}
              </Button>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

function FeedbackTab() {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('eng.feedback.manage');
  const openResponse = useOpenResponse();
  const [status, setStatus] = useState<EngFeedbackCaseStatus | ''>('Open');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [closing, setClosing] = useState<EngFeedbackCaseView | null>(null);
  const cases = useQuery({
    queryKey: [...surveyKeys.all(session?.tenantId), 'feedback', status, cursor],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/engagement/feedback-cases', {
          params: { query: { status: status || undefined, cursor, limit: 50 } },
        }),
      ),
    placeholderData: keepPreviousData,
  });

  const columns: DataTableColumn<EngFeedbackCaseView>[] = [
    {
      id: 'created',
      headerKey: 'eng:feedback.columns.created',
      cell: (row) => <JalaliDate value={row.createdAt} withTime />,
    },
    { id: 'patient', headerKey: 'eng:feedback.columns.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'branch', headerKey: 'eng:feedback.columns.branch', cell: (row) => row.branchName ?? '—' },
    { id: 'nps', headerKey: 'eng:feedback.columns.nps', cell: (row) => <NpsChip score={row.npsScore} /> },
    {
      id: 'ticket',
      headerKey: 'eng:feedback.columns.ticket',
      cell: (row) =>
        row.ticket ? (
          <Button
            size="small"
            component={RouterLink}
            to={`/tickets/${row.ticket.id}`}
            data-testid={`feedback-ticket-${row.id}`}
          >
            {t('eng:feedback.ticket', { number: faDigits(Number(row.ticket.number)) })}
          </Button>
        ) : (
          <Typography variant="caption">{t('eng:feedback.ticket_pending')}</Typography>
        ),
    },
    {
      id: 'status',
      headerKey: 'eng:feedback.columns.status',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <StatusChip
            tone={row.status === 'Open' ? 'pending' : 'done'}
            labelKey={`eng:feedback_status.${row.status}`}
          />
          {row.ackSent && <Chip size="small" variant="outlined" label={t('eng:feedback.ack_sent')} />}
        </Stack>
      ),
    },
    {
      id: 'actions',
      headerKey: 'eng:feedback.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          <Button size="small" onClick={() => openResponse(row.responseId)}>
            {t('eng:surveys.open')}
          </Button>
          {row.status === 'Open' && write.canWrite && (
            <Button size="small" onClick={() => setClosing(row)} data-testid={`feedback-close-${row.id}`}>
              {t('eng:feedback.close')}
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <WriteNotice write={write} />
      <Alert severity="info">{t('eng:feedback.hint')}</Alert>
      <TextField
        select
        size="small"
        label={t('eng:feedback.status_filter')}
        value={status}
        onChange={(event) => {
          setCursor(undefined);
          setStatus(event.target.value as EngFeedbackCaseStatus | '');
        }}
        sx={{ maxWidth: 220 }}
      >
        <MenuItem value="">{t('eng:common.all')}</MenuItem>
        <MenuItem value="Open">{t('eng:feedback_status.Open')}</MenuItem>
        <MenuItem value="Closed">{t('eng:feedback_status.Closed')}</MenuItem>
      </TextField>
      <DataTable
        captionKey="eng:feedback.caption"
        columns={columns}
        rows={cases.data?.items}
        getRowId={(row) => row.id}
        isLoading={cases.isPending}
        error={cases.error}
        onRetry={() => void cases.refetch()}
        hasMore={Boolean(cases.data?.nextCursor)}
        onLoadMore={() => setCursor(cases.data?.nextCursor ?? undefined)}
        emptyTitleKey="eng:feedback.empty"
      />
      {closing && <CloseFeedbackDialog item={closing} onClose={() => setClosing(null)} />}
    </Stack>
  );
}

function CloseFeedbackDialog({ item, onClose }: { item: EngFeedbackCaseView; onClose: () => void }) {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [submitError, setSubmitError] = useState<unknown>();
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/engagement/feedback-cases/{id}:close', {
          params: { path: { id: item.id } },
          body: { note: note.trim() || null },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: surveyKeys.all(session?.tenantId) });
      onClose();
    },
    onError: (error) => setSubmitError(error),
  });
  return (
    <FormDialog
      open
      maxWidth="xs"
      screenId="SCR-STAFF-967-CLOSE"
      titleKey="eng:feedback.close_title"
      onSubmit={() => save.mutate()}
      onClose={onClose}
      isDirty={note.length > 0}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <Typography variant="body2">{t('eng:feedback.close_hint')}</Typography>
        <TextField
          label={t('eng:feedback.note')}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          helperText={t('eng:common.no_clinical_hint')}
          multiline
          minRows={2}
          fullWidth
          slotProps={{ htmlInput: { maxLength: 1000, 'data-testid': 'feedback-close-note' } }}
        />
      </Stack>
    </FormDialog>
  );
}

function SurveyListTab() {
  const { t, i18n } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('eng.survey.manage');
  const [editing, setEditing] = useState<EngSurveyView | 'new' | null>(null);
  const surveys = useQuery({
    queryKey: [...surveyKeys.all(session?.tenantId), 'list'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/engagement/surveys')),
  });
  const activate = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      unwrap(
        active
          ? tenantApi.POST('/api/v1/engagement/surveys/{id}:activate', { params: { path: { id } } })
          : tenantApi.POST('/api/v1/engagement/surveys/{id}:deactivate', { params: { path: { id } } }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: surveyKeys.all(session?.tenantId) }),
  });

  return (
    <Stack spacing={2}>
      <WriteNotice write={write} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {t('eng:surveys.list_hint')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!write.canWrite}
          onClick={() => setEditing('new')}
          data-testid="survey-create"
        >
          {t('eng:surveys.add')}
        </Button>
      </Stack>
      {activate.error && <ErrorState error={activate.error} compact />}
      {surveys.isPending && <LoadingState rows={3} />}
      {surveys.error && <ErrorState error={surveys.error} onRetry={() => void surveys.refetch()} />}
      {surveys.data?.length === 0 && (
        <Alert severity="info" data-testid="survey-default-hint">
          {t('eng:surveys.default_hint')}
        </Alert>
      )}
      {surveys.data?.map((survey) => (
        <Paper key={survey.id} variant="outlined" sx={{ p: 2 }} data-testid={`survey-${survey.id}`}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1">{survey.name}</Typography>
                {survey.isActive && <StatusChip tone="done" labelKey="eng:surveys.active" />}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {t('eng:surveys.summary', {
                  questions: faDigits(survey.questions.length),
                  sent: faDigits(Number(survey.dispatches)),
                })}
                {survey.formTemplateName
                  ? ` · ${t('eng:surveys.template_name', { name: survey.formTemplateName, version: faDigits(Number(survey.formTemplateVersion ?? 1)) })}`
                  : ''}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                disabled={!write.canWrite}
                onClick={() => setEditing(survey)}
                data-testid={`survey-edit-${survey.id}`}
              >
                {t('eng:surveys.edit')}
              </Button>
              <Button
                size="small"
                disabled={!write.canWrite || activate.isPending}
                onClick={() => activate.mutate({ id: survey.id, active: !survey.isActive })}
                data-testid={`survey-${survey.isActive ? 'deactivate' : 'activate'}-${survey.id}`}
              >
                {t(survey.isActive ? 'eng:surveys.deactivate' : 'eng:surveys.activate')}
              </Button>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {survey.questions.map((question) => (
              <Chip
                key={question.key}
                size="small"
                variant={question.standard ? 'filled' : 'outlined'}
                label={surveyText(question.label, i18n.language)}
              />
            ))}
          </Stack>
          {survey.skippedFields.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t('eng:surveys.skipped_fields', {
                count: survey.skippedFields.length,
                fields: survey.skippedFields.join(t('common:list_separator')),
              })}
            </Alert>
          )}
        </Paper>
      ))}
      {editing && (
        <SurveyDialog survey={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </Stack>
  );
}

function SurveyDialog({ survey, onClose }: { survey: EngSurveyView | null; onClose: () => void }) {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [name, setName] = useState(survey?.name ?? '');
  const [template, setTemplate] = useState<EngSurveyTemplateOption | null>(null);
  const [touched, setTouched] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();
  const templates = useQuery({
    queryKey: [...surveyKeys.all(session?.tenantId), 'templates'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/engagement/survey-templates')),
  });
  const selected = template ?? templates.data?.find((item) => item.id === survey?.formTemplateId) ?? null;
  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), formTemplateId: selected?.id ?? null };
      return unwrap(
        survey
          ? tenantApi.PUT('/api/v1/engagement/surveys/{id}', { params: { path: { id: survey.id } }, body })
          : tenantApi.POST('/api/v1/engagement/surveys', {
              body,
              headers: { 'Idempotency-Key': idempotency.current() },
            }),
      );
    },
    onMutate: () => setSubmitError(undefined),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: surveyKeys.all(session?.tenantId) });
      onClose();
    },
    onError: (error) => setSubmitError(error),
  });
  const nameMissing = touched && !name.trim();
  return (
    <FormDialog
      open
      maxWidth="sm"
      screenId="SCR-STAFF-967-SURVEY"
      titleKey={survey ? 'eng:surveys.edit_title' : 'eng:surveys.new_title'}
      onSubmit={() => {
        setTouched(true);
        if (name.trim()) {
          save.mutate();
        }
      }}
      onClose={onClose}
      isDirty={name !== (survey?.name ?? '') || template !== null}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <Typography variant="body2">{t('eng:surveys.standard_hint')}</Typography>
        <TextField
          label={t('eng:surveys.name')}
          value={name}
          required
          onChange={(event) => setName(event.target.value)}
          error={nameMissing}
          helperText={nameMissing ? t('common:validation.required') : undefined}
          slotProps={{ htmlInput: { maxLength: 200, 'data-testid': 'survey-name' } }}
        />
        <Autocomplete<EngSurveyTemplateOption, false, false, false>
          value={selected}
          onChange={(_event, next) => setTemplate(next)}
          options={templates.data ?? []}
          loading={templates.isPending}
          getOptionLabel={(option) => `${option.name} · v${option.version}`}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText={t('eng:surveys.no_templates')}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.name} · v{faDigits(Number(option.version))}
              {option.isSample && (
                <Box component="span" sx={{ marginInlineStart: 1 }}>
                  <SampleBadge />
                </Box>
              )}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('eng:surveys.template')}
              helperText={t('eng:surveys.template_hint')}
              inputProps={{ ...params.inputProps, 'data-testid': 'survey-template' }}
            />
          )}
        />
      </Stack>
    </FormDialog>
  );
}

function SettingsTab() {
  const { tenantApi, session } = useTenantSession();
  const settings = useQuery({
    queryKey: [...surveyKeys.all(session?.tenantId), 'settings'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/engagement/survey-settings')),
  });
  if (settings.isPending) {
    return <LoadingState rows={4} />;
  }
  if (settings.error || !settings.data) {
    return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;
  }
  return <SettingsForm key={String(settings.data.rowVersion)} initial={settings.data} />;
}

function SettingsForm({ initial }: { initial: EngSurveySettingsView }) {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('eng.survey.manage');
  const [enabled, setEnabled] = useState(initial.enabled);
  const [delayHours, setDelayHours] = useState(String(initial.delayHours));
  const [cooldownDays, setCooldownDays] = useState(String(initial.cooldownDays));
  const [linkValidDays, setLinkValidDays] = useState(String(initial.linkValidDays));
  const [includePublicPage, setIncludePublicPage] = useState(initial.includePublicPage);
  const [links, setLinks] = useState<EngReviewLink[]>(initial.reviewLinks);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/engagement/survey-settings', {
          body: {
            enabled,
            delayHours: Number(latinDigits(delayHours)),
            cooldownDays: Number(latinDigits(cooldownDays)),
            linkValidDays: Number(latinDigits(linkValidDays)),
            includePublicPage,
            reviewLinks: links.filter((link) => link.label.trim() && link.url.trim()),
            rowVersion: Number(initial.rowVersion) || null,
          },
        }),
      ),
    onSuccess: (saved) => queryClient.setQueryData([...surveyKeys.all(session?.tenantId), 'settings'], saved),
  });
  const numberField = (key: string, value: string, onChange: (value: string) => void, testId: string) => (
    <TextField
      size="small"
      label={t(`eng:survey_settings.${key}`)}
      helperText={t(`eng:survey_settings.${key}_hint`)}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={!write.canWrite}
      slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', 'data-testid': testId } }}
      sx={{ maxWidth: 260 }}
    />
  );
  return (
    <Stack
      spacing={2}
      component="form"
      onSubmit={(event) => (event.preventDefault(), save.mutate())}
      data-testid="survey-settings"
    >
      <WriteNotice write={write} />
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
            disabled={!write.canWrite}
          />
        }
        label={t('eng:survey_settings.enabled')}
      />
      {numberField('delay_hours', delayHours, setDelayHours, 'survey-delay')}
      {numberField('cooldown_days', cooldownDays, setCooldownDays, 'survey-cooldown')}
      {numberField('link_valid_days', linkValidDays, setLinkValidDays, 'survey-link-valid')}
      <Alert severity="info">{t('eng:survey_settings.quiet_hours')}</Alert>
      <Typography variant="subtitle2">{t('eng:survey_settings.review_title')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('eng:survey_settings.review_hint')}
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={includePublicPage}
            onChange={(event) => setIncludePublicPage(event.target.checked)}
            disabled={!write.canWrite}
          />
        }
        label={t('eng:survey_settings.include_public_page')}
      />
      {initial.publicPageUrl && (
        <Typography variant="caption" dir="ltr" sx={{ alignSelf: 'flex-start' }}>
          {initial.publicPageUrl}
        </Typography>
      )}
      {initial.profileMapUrl && (
        <Typography variant="caption" color="text.secondary">
          {t('eng:survey_settings.profile_map')}: <span dir="ltr">{initial.profileMapUrl}</span>
        </Typography>
      )}
      {links.map((link, index) => (
        <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            label={t('eng:survey_settings.link_label')}
            value={link.label}
            disabled={!write.canWrite}
            onChange={(event) =>
              setLinks(links.map((item, i) => (i === index ? { ...item, label: event.target.value } : item)))
            }
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
          <TextField
            size="small"
            label={t('eng:survey_settings.link_url')}
            value={link.url}
            disabled={!write.canWrite}
            onChange={(event) =>
              setLinks(links.map((item, i) => (i === index ? { ...item, url: event.target.value } : item)))
            }
            slotProps={{ htmlInput: { dir: 'ltr', maxLength: 500 } }}
            sx={{ flex: 1 }}
          />
          <IconButton
            aria-label={t('eng:survey_settings.remove_link')}
            disabled={!write.canWrite}
            onClick={() => setLinks(links.filter((_item, i) => i !== index))}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      ))}
      <Button
        size="small"
        sx={{ alignSelf: 'flex-start' }}
        disabled={!write.canWrite || links.length >= 5}
        onClick={() => setLinks([...links, { label: '', url: '' }])}
      >
        {t('eng:survey_settings.add_link')}
      </Button>
      {save.error && <ErrorState error={save.error} compact />}
      {save.isSuccess && <Alert severity="success">{t('eng:survey_settings.saved')}</Alert>}
      <Button
        type="submit"
        variant="contained"
        disabled={!write.canWrite || save.isPending}
        sx={{ alignSelf: 'flex-start' }}
        data-testid="survey-settings-save"
      >
        {t('eng:survey_settings.save')}
      </Button>
    </Stack>
  );
}
